import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IssueEnvironment, SweepProgress } from '../../api/issues';
import { age, ago, date, dateAgo, day, moment } from './issueFormat';
import { LiveExactLimits } from '../Rollout/LiveExactLimits';
import { ReevaluateOptInToggle } from './ReevaluateOptInToggle';
import { SweepOptInToggle } from './SweepOptInToggle';

export interface IEnvironmentCardProps {
    environment: IssueEnvironment;
    sweeping: boolean;
    /** How far the scan that runs now is. */
    progress?: SweepProgress | null;
    onSweep: () => void;
    onMail: () => void;
}

/**
 * One environment at a glance, above its issues: how old the open issues are, how far the
 * scanner got, whether the scanner's timer and a deploy take it along, and the way to its mail,
 * its own scan and its error log. The counts per status are the filter buttons underneath.
 */
export const EnvironmentCard = ({ environment: e, sweeping, progress, onSweep, onMail }: IEnvironmentCardProps) => (
    <section className={`env-card env-card--${e.stage}`} aria-label={`${e.customer} / ${e.environment}`}>
        <div className="env-card__head">
            <h3 className="env-card__title">
                {e.customer} / {e.environment}
                <span className={`env-card__stage env-card__stage--${e.stage}`}>{e.stage === 'prod' ? 'productie' : 'test'}</span>
            </h3>
            <LiveExactLimits customer={e.customer} environment={e.environment} />
            <div className="issues__actions">
                <button
                    type="button"
                    className={`btn btn-sm btn-outline-primary env-card__scan${sweeping ? ' is-running' : ''}`}
                    onClick={onSweep}
                    disabled={sweeping}
                    style={{ ['--progress' as string]: `${progress?.percent ?? 0}%` }}
                    title={sweeping ? progress?.phase ?? 'Scan wordt gestart' : 'Deze omgeving nu scannen'}
                >
                    <FontAwesomeIcon icon="rotate" spin={sweeping} />{' '}
                    {sweeping ? `Scannen ${progress?.percent ?? 0}%` : 'Nu scannen'}
                </button>
                {e.waitingOnCustomer > 0 && (
                    <button
                        type="button"
                        className={`btn btn-sm ${e.customerNotTold > 0 ? 'btn-warning' : 'btn-outline-secondary'}`}
                        onClick={onMail}
                        title={e.customerNotTold > 0 ? `${e.customerNotTold} punten die de klant nog niet kent` : 'De klant kent alles al'}
                    >
                        <FontAwesomeIcon icon="envelope" /> Klantmail{e.customerNotTold > 0 ? ` (${e.customerNotTold} nieuw)` : ''}
                    </button>
                )}
                <a
                    className="btn btn-sm btn-outline-secondary"
                    href={`/dashboard/${encodeURIComponent(e.customer)}/${encodeURIComponent(e.environment)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FontAwesomeIcon icon="arrow-up-right-from-square" /> Foutenlogboek
                </a>
            </div>
        </div>

        {sweeping && (
            <p className="env-card__phase issues__muted">
                <FontAwesomeIcon icon="spinner" spin /> {progress?.phase ?? 'scan wordt gestart'}
            </p>
        )}

        <dl className="env-card__facts">
            <div>
                <dt>Oudste open</dt>
                <dd title={e.open > 0 ? dateAgo(e.oldestOpenAt) : undefined}>{e.open > 0 ? age(e.oldestOpenAt) : '–'}</dd>
            </div>
            <div>
                <dt>Laatste fout</dt>
                <dd title={e.open > 0 ? dateAgo(e.latestOpenFailureAt) : undefined}>{e.open > 0 ? ago(e.latestOpenFailureAt) : '–'}</dd>
            </div>
            <div>
                <dt>Gescand</dt>
                <dd title={e.lastSuccessAt ? ago(e.lastSuccessAt) : undefined}>{e.lastSuccessAt ? moment(e.lastSuccessAt) : 'nog niet'}</dd>
            </div>
            <div>
                <dt>Vorige scan</dt>
                <dd>{lastScan(e)}</dd>
            </div>
            <div>
                <dt>Scantimer</dt>
                <dd>
                    <SweepOptInToggle customer={e.customer} environment={e.environment} />
                </dd>
            </div>
            <div>
                <dt>Na deploy</dt>
                <dd>
                    <ReevaluateOptInToggle customer={e.customer} environment={e.environment} />
                </dd>
            </div>
            {e.swept && !e.backfillDone && (
                <div>
                    <dt>Historie</dt>
                    <dd>
                        <History environment={e} />
                    </dd>
                </div>
            )}
        </dl>

        {e.lastError && (
            <p className="issues__error">
                <FontAwesomeIcon icon="triangle-exclamation" /> Scan mislukt op {date(e.lastRunAt)}. {e.lastError}
            </p>
        )}
    </section>
);

/** "niets veranderd (2 s)", "3 nieuw, 1 opgelost (2 s)". */
const lastScan = (e: IssueEnvironment): string => {
    if (!e.swept) {
        return 'nog niet';
    }

    const seconds = e.lastDurationMs > 0 ? ` (${Math.round(e.lastDurationMs / 1000)} s)` : '';
    const changes = [e.lastFound > 0 ? `${e.lastFound} nieuw` : '', e.lastResolved > 0 ? `${e.lastResolved} opgelost` : ''].filter(Boolean);

    return `${changes.length > 0 ? changes.join(' en ') : 'niets veranderd'}${seconds}`;
};

const dayOf = (yyyymmdd?: string | null): number | null =>
    yyyymmdd && yyyymmdd.length === 8
        ? Date.UTC(Number(yyyymmdd.slice(0, 4)), Number(yyyymmdd.slice(4, 6)) - 1, Number(yyyymmdd.slice(6, 8)))
        : null;

/**
 * How much of the past year the backlog holds. The first scans of an environment read that
 * year a few months per round; until they are through, older issues are not in the list yet.
 */
const History = ({ environment: e }: { environment: IssueEnvironment }) => {
    const from = dayOf(e.backfillFrom);
    const through = dayOf(e.watermark);
    // The scanner settles days up to two days ago; the rest is read every round anyway.
    const until = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()) - 2 * 86400000;
    const total = from !== null ? Math.max(1, (until - from) / 86400000) : null;
    const read = from !== null && through !== null ? Math.max(0, (through - from) / 86400000 + 1) : 0;
    const percent = total ? Math.min(100, Math.round((100 * read) / total)) : 0;

    return (
        <span
            className="env-card__history"
            title={`Ingelezen van ${day(e.backfillFrom)} tot en met ${day(e.watermark)}. Oudere issues staan er nog niet in.`}
        >
            <span className="scanner__bar" aria-hidden="true">
                <span style={{ width: `${percent}%` }} />
            </span>
            wordt ingelezen ({percent}%)
        </span>
    );
};
